import AsyncStorage from '@react-native-async-storage/async-storage';
import { createClient } from '@supabase/supabase-js';
import { supabase } from './client';
import { mergeProgress } from '@/utils/guides';

const queues = new Map();
const storageKey = accountID => `@reach:guides:${accountID}`;
const serialize = (accountID, operation) => {
    const next = (queues.get(accountID) || Promise.resolve()).catch(() => {}).then(operation);
    queues.set(accountID, next);
    return next.finally(() => { if (queues.get(accountID) === next) queues.delete(accountID); });
};

const accountClient = async (accountID) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!accountID || session?.user?.id !== accountID) throw new Error('Guide account changed');
    const token = session.access_token;
    return createClient(process.env.EXPO_PUBLIC_DB_URL, process.env.EXPO_PUBLIC_DB_KEY, {
        accessToken: async () => token,
        auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
    });
};

const readLocal = async (accountID) => {
    const stored = await AsyncStorage.getItem(storageKey(accountID));
    if (!stored) return { progress: [], pending: [], preference: null, autoGuidesEnabled: true };
    try {
        const parsed = JSON.parse(stored);
        const validRows = value => Array.isArray(value) ? value.filter(row => row && /^[a-z][a-z0-9_.-]{0,119}$/.test(row.featureID) && Number.isInteger(row.learningRevision) && row.learningRevision > 0 && ['viewed', 'skipped'].includes(row.status)) : [];
        return { progress: validRows(parsed.progress), pending: validRows(parsed.pending), preference: typeof parsed.preference === 'boolean' ? parsed.preference : null, autoGuidesEnabled: parsed.autoGuidesEnabled !== false };
    } catch {
        return { progress: [], pending: [], preference: null, autoGuidesEnabled: true };
    }
};

const saveLocal = (accountID, state) => AsyncStorage.setItem(storageKey(accountID), JSON.stringify(state));

const flush = async (accountID, state, client) => {
    while (state.pending.length) {
        const batch = state.pending.slice(0, 50);
        const { data, error } = await client.rpc('record_own_guide_progress', { p_items: batch });
        if (error) throw error;
        state.progress = mergeProgress(state.progress, data || []);
        state.pending = state.pending.slice(batch.length);
        await saveLocal(accountID, state);
    }
    if (state.preference !== null) {
        const { data, error } = await client.from('RegisteredUsers').update({ autoGuidesEnabled: state.preference }).eq('id', accountID).select('autoGuidesEnabled').single();
        if (error) throw error;
        state.autoGuidesEnabled = data.autoGuidesEnabled;
        state.preference = null;
        await saveLocal(accountID, state);
    }
    return state;
};

export const getGuideState = accountID => serialize(accountID, async () => {
    const state = await readLocal(accountID);
    const client = await accountClient(accountID);
    await flush(accountID, state, client);
    const [progress, preference] = await Promise.all([
        client.from('UserGuideProgress').select('*').eq('userID', accountID),
        client.from('RegisteredUsers').select('autoGuidesEnabled').eq('id', accountID).single(),
    ]);
    if (progress.error || preference.error) throw progress.error || preference.error;
    state.progress = mergeProgress(progress.data || [], state.pending);
    state.autoGuidesEnabled = preference.data.autoGuidesEnabled;
    await saveLocal(accountID, state);
    return { ...state, synced: true };
});

export const recordGuideProgress = (accountID, items) => serialize(accountID, async () => {
    const state = await readLocal(accountID);
    const changes = items.map(({ featureID, learningRevision, status }) => ({ featureID, learningRevision, status }));
    state.pending = mergeProgress(state.pending, changes);
    state.progress = mergeProgress(state.progress, changes);
    await saveLocal(accountID, state);
    try {
        await flush(accountID, state, await accountClient(accountID));
        return { ...state, synced: true };
    } catch {
        return { ...state, synced: false };
    }
});

export const setAutoGuidesEnabled = (accountID, enabled) => serialize(accountID, async () => {
    const state = await readLocal(accountID);
    state.preference = enabled;
    state.autoGuidesEnabled = enabled;
    await saveLocal(accountID, state);
    try {
        await flush(accountID, state, await accountClient(accountID));
        return { ...state, synced: true };
    } catch {
        return { ...state, synced: false };
    }
});
